<template>
	<label
		:key="role?.id"
		:for="getInputLabel(role?.id)"
		class="user-assign-select-role-item"
		:class="avatarClassAddons"
	>
		<button
			v-tooltip="filterByRoleLabel"
			type="button"
			class="user-assign-select-role-item__avatar"
			:class="buttonClassAddons"
			:style="{ '--role-color': role.hslColor ?? '' }"
			:aria-label="filterByRoleLabel"
			@click.stop="emit('update:active-role-filter-id')"
		>
			<SuiAvatar
				size="x-small"
				variant="dashed"
				show-border
				:animated="isFilterActive"
				:color="role.hslColor ?? ''"
				:initials="roleInitials"
			>
				<span class="user-assign-select-role-item__avatar-initials">
					{{ roleInitials }}
				</span>
			</SuiAvatar>

			<SuiIcon
				v-if="!isFilterActive"
				class="user-assign-select-role-item__hover-icon"
				name="icon-circlearrow-16"
			/>
		</button>

		<SuiTimeInput
			v-if="hasIndividualTimeAllocation && hasCustomAllocation"
			:id="getInputLabel(role?.id)"
			:model-value="allocatedTime"
			class="user-assign-select-role-item__time-input"
			size="xsmall"
			variant="unstyled"
			align="center"
			hide-clear-button
			show-zero-values
			@update:model-value="emit('update:allocated-time', $event)"
			@click="$event.target.select()"
			@focus="emit('focus')"
			@blur="emit('blur')"
			@keyup.enter="$event.target.blur()"
		/>
	</label>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SuiTimeInput, SuiAvatar, SuiIcon } from '@developers/scoro-ui';
import { useTranslation } from '@/composables';
import { type TaskAssigneeAllocatedDuration } from '@/features/Task/components/TaskUsers/types';
import type { UserAssignSelectRole } from '../../../types';
import { getInitials } from '../../../utils';

export interface UserAssignSelectHeaderRoleItemProps {
	role: UserAssignSelectRole;
	hasIndividualTimeAllocation: boolean;
	hasCustomAllocation: boolean;
	allocatedTime: string;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	activeRoleFilterId?: UserAssignSelectRole['id'] | null;
}

const props = defineProps<UserAssignSelectHeaderRoleItemProps>();

const emit = defineEmits<{
	(e: 'update:allocated-time', value: string): void;
	(e: 'focus'): void;
	(e: 'blur'): void;
	(e: 'update:active-role-filter-id'): void;
}>();

const { t } = useTranslation();

const roleInitials = computed(
	() => props.role.initials || getInitials(props.role.name)
);

const isFilterActive = computed(
	() => props.activeRoleFilterId === props.role.id
);
const isDimmed = computed(
	() => props.activeRoleFilterId !== null && !isFilterActive.value
);

const filterByRoleLabel = computed(() =>
	t('assign_to_role', { name: props.role.name })
);

const avatarClassAddons = computed(() => ({
	'user-assign-select-role-item--with-time-input':
		props.hasCustomAllocation && props.hasIndividualTimeAllocation,
	'user-assign-select-role-item--dimmed': isDimmed.value,
}));

const buttonClassAddons = computed(() => ({
	'user-assign-select-role-item__avatar--active': isFilterActive.value,
}));

const getInputLabel = (roleId: UserAssignSelectRole['id']) => {
	return `time-input-role-${roleId}`;
};
</script>

<style lang="scss" scoped>
.user-assign-select-role-item {
	$parent: &;
	display: flex;
	align-items: center;
	border-radius: 2.5rem;

	&--with-time-input {
		background: $neutral-00;
		box-shadow: $scoro-box-shadow-small;
	}

	&--dimmed {
		filter: #{'grayscale(1)'};
		opacity: 0.5;
		pointer-events: none;
	}

	&__avatar {
		position: relative;

		&:not(&--active):hover {
			#{$parent}__avatar-initials {
				background-color: var(--role-color);
			}

			#{$parent}__hover-icon {
				opacity: 1;
			}
		}
	}

	&__avatar-initials {
		transition: background-color 0.3s ease-out;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		border-radius: 50%;
	}

	&__hover-icon {
		--sui-icon-fill: #{$neutral-00};

		opacity: 0;
		position: absolute;
		inset: 0;
		margin: auto;
		z-index: 10;
		transition: opacity 0.3s ease-out;
	}

	&__time-input {
		:deep(.sui-input) {
			--sui-input-color-text: #{$neutral-80};
			--sui-input-bg-color: transparent;

			width: 8.125rem;
			border-radius: 2.5rem;
		}

		:deep(.sui-input__field) {
			&:hover {
				background-color: $neutral-20;
				border-radius: 2.5rem;
				margin: 0.25rem;
			}

			&:hover:not(:focus) {
				cursor: pointer;
			}

			&:focus {
				background-color: $neutral-20;
				border-radius: 2.5rem;
				margin: 0.125rem;
				border: 1px solid $neutral-30;
			}
		}
	}
}
</style>
